import { useCallback } from 'react';
import { prop } from 'ramda';

import { TAppTag } from '@core/js/components/system/TagManager/types';
import { useIsAdmin } from '@core/js/hooks/user/hooks/useIsAdmin';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';

import { TAG_TYPE_SUN } from '@interlayer/js/components/system/TagManager/sun/constants';
import { TAppSegmentationTag } from '@interlayer/js/components/system/TagManager/sun/types';
import { useCustomUserSelector } from '@interlayer/js/custom/stores/user/useCustomUserSelector';
import { transformStrToArray } from '@interlayer/js/custom/utils/methods.util';

import {
    RULE_MODIFIER,
    RULE_MODIFIER_MAP,
    SEGMENTATION_MODE,
    TIME_PERIODS,
    USER_VISIBILITY
} from './constants';
import { convertDate, getCurrentDateObj } from './helpers';
import {
    TDateTimeRange,
    TPlayerTagsConfig,
    TScheduleData,
    TSchedulePeriod,
    TSegmentationByTag,
    TSegmentationConfig,
    TSegmentationCustomRules,
    TSegmentationRule
} from './types';

const checkRangeDate = ({ fromDate, fromTime, toDate, toTime }: TDateTimeRange): boolean => {
    const fromDateInMS = convertDate(fromDate, fromTime);
    const toDateInMS = convertDate(toDate, toTime);
    const currentTime = Date.now();

    return currentTime >= fromDateInMS && currentTime <= toDateInMS;
};

const checkPeriod = (scheduleData: TSchedulePeriod): boolean => {
    switch (scheduleData.period) {
        case TIME_PERIODS.RANGE: {
            return checkRangeDate(scheduleData);
        }

        case TIME_PERIODS.DAILY: {
            const { fullDate } = getCurrentDateObj();
            const { fromTime, toTime } = scheduleData;

            return checkRangeDate({
                fromTime,
                toTime,
                fromDate: fullDate,
                toDate: fullDate
            });
        }

        case TIME_PERIODS.WEEKLY: {
            const { weekDayLong } = getCurrentDateObj();
            const { days } = scheduleData;

            return days.includes(weekDayLong);
        }

        case TIME_PERIODS.MONTHLY: {
            const { dd } = getCurrentDateObj();
            const { months } = scheduleData;

            return months.map(Number).includes(Number(dd));
        }

        case TIME_PERIODS.YEARLY: {
            const { dd, mm } = getCurrentDateObj();
            const { dateOfYear } = scheduleData;

            return dateOfYear === `${dd}-${mm}`;
        }

        default:
            return true;
    }
};

const filterByDate = (schedule: TScheduleData[]): boolean => {
    if (!schedule) {
        return true;
    }

    const { ranges, periods } = schedule.reduce((acc, item) => {
        if (item.period === TIME_PERIODS.RANGE) {
            acc.ranges.push(item);
        } else {
            acc.periods.push(item);
        }

        return acc;
    }, { ranges: [], periods: [] } as { ranges: TScheduleData[]; periods: TScheduleData[] });

    return periods.every(checkPeriod)
        && (ranges.length ? ranges.some(item => checkPeriod(item)) : true);
};

const useFilterByUserRole = () => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));

    return useCallback((visibility: USER_VISIBILITY): boolean => {
        if (!visibility) {
            return true;
        }

        switch (visibility) {
            case USER_VISIBILITY.all:
                return true;
            case USER_VISIBILITY.guest:
                return !isLoggedIn;
            case USER_VISIBILITY.user:
                return isLoggedIn;
            default:
                return true;
        }
    }, [isLoggedIn]);
};

export const useFilterByPlayerTags = () => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const allPlayerTags = useCustomUserSelector(prop('allPlayerTags'));

    return useCallback(({
        playerTags,
        isAllPlayerTagExist,
        playerTagValues,
        isAllValueExistInPlayerTags
    }: TPlayerTagsConfig) => {
        if (!isLoggedIn || !playerTags) {
            return true;
        }

        if (!allPlayerTags) {
            return false;
        }

        const playerTagsArray = transformStrToArray(playerTags);
        const playerTagValuesArray = transformStrToArray(playerTagValues, [',', ';']);

        return playerTagsArray[isAllPlayerTagExist ? 'every' : 'some'](playerTag => {
            const playerTagData = allPlayerTags[playerTag];

            if (!playerTagData) {
                return false;
            }

            if (!playerTagValuesArray?.length) {
                return true;
            }

            const playerTagDataValueArray = transformStrToArray(playerTagData.value as string, [';', ',']);

            return playerTagValuesArray[isAllValueExistInPlayerTags ? 'every' : 'some'](tagValue => playerTagDataValueArray.includes(tagValue));
        });
    }, [allPlayerTags, isLoggedIn]);
};

const useCheckRule = () => {
    const filterByPlayerTags = useFilterByPlayerTags();
    const filterByUserRole = useFilterByUserRole();

    return useCallback(({
        modifier = RULE_MODIFIER.and,
        visibility,
        playerTagsConfig,
        schedule
    }: TSegmentationRule) => {
        const check = filterByUserRole(visibility)
            && filterByPlayerTags(playerTagsConfig)
            && filterByDate(schedule);

        return {
            modifier: RULE_MODIFIER_MAP[modifier],
            check: [RULE_MODIFIER.orNot, RULE_MODIFIER.andNot].includes(modifier) ? !check : check
        };
    }, [filterByPlayerTags, filterByUserRole]);
};

const getSegmentationOptions = (segmentationConfig: TSegmentationConfig, tags: TAppTag[]): TSegmentationRule[] => {
    const getConfig = (): TSegmentationByTag | TSegmentationCustomRules => {
        if ('segmentationMode' in segmentationConfig && segmentationConfig.segmentationMode) {
            return segmentationConfig;
        }

        return {
            tagName: segmentationConfig.tagName,
            segmentationMode: SEGMENTATION_MODE.segmentationTag
        };
    };

    const config = getConfig();

    if (config.segmentationMode === SEGMENTATION_MODE.customRules) {
        return config.segmentation || [];
    }

    if (!config?.tagName) {
        return [];
    }

    return tags
        ?.find((tag): tag is TAppSegmentationTag => {
            return tag.tagTypeId === TAG_TYPE_SUN.segmentation
                && tag.name.trim() === config.tagName.trim();
        })
        ?.config.segmentation || [];
};

export const useFilterSegmentation = () => {
    const isAdmin = useIsAdmin();
    const checkRule = useCheckRule();
    const tags = useAppSelector(prop('tags'));

    return useCallback((segmentation?: TSegmentationConfig) => {
        if (isAdmin || !segmentation) {
            return true;
        }

        const { orRules, andRules } = getSegmentationOptions(segmentation, tags)
            .reduce((acc, rule) => {
                const { modifier, check } = checkRule(rule);

                if (modifier === RULE_MODIFIER_MAP.or) {
                    acc.orRules.push(check);
                } else {
                    acc.andRules.push(check);
                }

                return acc;
            }, { orRules: [], andRules: [] } as { orRules: boolean[]; andRules: boolean[] });

        return orRules.some(Boolean) || andRules.every(Boolean);
    }, [checkRule, isAdmin, tags]);
};
